import numeral from 'numeral';
import React, { useEffect } from 'react';
/** import third-party libraries in the section above, keep the ascending order */

import Typography from '@mui/material/Typography';
/** import mui components in the section above, keep the ascending order */

import { Label } from 'widgets-v5/typography';
import AccordionItem from 'widgets-v5/expansion-panel-item';
import Spacer from 'widgets-v5/spacer';
import { TextField } from 'widgets-v6/standard-input';
import TextFieldFormatter from 'widgets-v6/text-field-formatter';
/** import widgets-v5 components in the section above, keep the ascending order */

import { EXCHANGE_LABEL_MAPPING_FOR_PG_ADS } from 'forms/ad-form/constants';
import { Name } from 'forms/ad-form/modules/basic';
import { SingleSelect } from 'widgets-v6/select';
/** import widgets-v6 components in the section above, keep the ascending order */

import { DealType, DealCategory } from 'common/constants/deals';
import InfoAlertDealPrice from '../info-alert-deal-price';
/** import classes and others in the section above, keep the ascending order */

export default function GuaranteedDeal({
    expandPanel,
    expanded,
    formData,
    dealsByOrganization,
    onFieldChange,
    errors,
    showErrors,
    campaign,
    mode,
}) {
    const exchangeOptions = EXCHANGE_LABEL_MAPPING_FOR_PG_ADS.filter(
        ({ currency }) => currency === campaign.currency
    ).map(({ label }) => ({ label, value: label }));

    const advertisers = campaign.clients.filter(client => client.type === 'advertiser');

    const onChangeGuaranteedDeal = (field, value) => {
        const updatedDealObject = {
            ...formData.guaranteed_deal,
            [field]: value,
        };
        onFieldChange('guaranteed_deal')(updatedDealObject);
    };

    const { guaranteed_deal = {}, start, end } = formData || {};

    const getGuaranteedDealFieldError = field => {
        return errors[`guaranteed_deal.${field}`];
    };

    useEffect(() => {
        const guaranteedDeal = dealsByOrganization.find(deal => deal.id === formData.deals[0]);
        const updatedDealObject = {
            ...guaranteed_deal,
            type: DealType.Guaranteed_Deal,
            category: DealCategory.Guaranteed,
            currency: campaign.currency,
            start_date: start || null,
            end_date: end || null,
            advertiserId: advertisers.length === 1 ? advertisers[0].organization : null,
            app_name: guaranteedDeal?.app_name || guaranteed_deal?.app_name || '',
            id: guaranteedDeal?.id || guaranteed_deal?.id || '',
            pub_name: guaranteedDeal?.pub_name || guaranteed_deal?.pub_name || '',
            supply_source: guaranteedDeal?.supply_source || guaranteed_deal?.supply_source || '',
            price_local: guaranteedDeal?.price_local || guaranteed_deal?.price_local || 0,
        };
        // Set supply_source if only one option and not already set
        if (exchangeOptions.length === 1 && !updatedDealObject.supply_source) {
            updatedDealObject.supply_source = exchangeOptions[0].value;
        }
        onFieldChange('guaranteed_deal')(updatedDealObject);
    }, [start, end]);

    return (
        <React.Fragment>
            <Typography>Guaranteed Deal</Typography>
            <Spacer />
            <AccordionItem
                onClick={expandPanel('pgDealName')}
                expanded={expanded === 'pgDealName'}
                readOnly={mode === 'edit'}
                title="Deal Name"
                required
                incomplete={!showErrors && !guaranteed_deal.app_name}
                error={showErrors && getGuaranteedDealFieldError('app_name')}
                details={
                    <Name
                        value={guaranteed_deal.app_name}
                        onFieldChange={name => onChangeGuaranteedDeal('app_name', name)}
                        errors={showErrors && getGuaranteedDealFieldError('app_name')}
                        showErrors={showErrors}
                    />
                }
                summary={
                    guaranteed_deal.app_name ? (
                        <Label>{guaranteed_deal.app_name}</Label>
                    ) : (
                        <Label placeholder>Add a Deal Name</Label>
                    )
                }
            />
            <AccordionItem
                onClick={expandPanel('pgDealId')}
                expanded={expanded === 'pgDealId'}
                readOnly={mode === 'edit'}
                title="Deal ID"
                required
                incomplete={!showErrors && !guaranteed_deal.deal_id}
                error={showErrors && getGuaranteedDealFieldError('id')}
                details={
                    <Name
                        value={guaranteed_deal.id}
                        onFieldChange={name => onChangeGuaranteedDeal('id', name)}
                        errors={showErrors && getGuaranteedDealFieldError('id')}
                        showErrors={showErrors}
                    />
                }
                summary={
                    guaranteed_deal.id ? (
                        <Label>{guaranteed_deal.id}</Label>
                    ) : (
                        <Label placeholder>Add a Deal ID</Label>
                    )
                }
            />
            <AccordionItem
                onClick={expandPanel('pgPublisherName')}
                expanded={expanded === 'pgPublisherName'}
                readOnly={mode === 'edit'}
                title="Publisher Name"
                required
                incomplete={!showErrors && !guaranteed_deal.pub_name}
                error={showErrors && getGuaranteedDealFieldError('pub_name')}
                details={
                    <Name
                        value={formData.pgPublisherName}
                        onFieldChange={name => onChangeGuaranteedDeal('pub_name', name)}
                        errors={showErrors && getGuaranteedDealFieldError('pub_name')}
                        showErrors={showErrors}
                    />
                }
                summary={
                    guaranteed_deal.pub_name ? (
                        <Label>{guaranteed_deal.pub_name}</Label>
                    ) : (
                        <Label placeholder>Add a publisher name</Label>
                    )
                }
            />
            <AccordionItem
                onClick={expandPanel('pgSupplySource')}
                expanded={expanded === 'pgSupplySource'}
                readOnly={mode === 'edit'}
                title={'Exchange'}
                required
                error={showErrors && getGuaranteedDealFieldError('supply_source')}
                details={
                    <div>
                        <SingleSelect
                            value={guaranteed_deal.supply_source}
                            error={showErrors && getGuaranteedDealFieldError('supply_source')}
                            options={exchangeOptions}
                            onChange={value => onChangeGuaranteedDeal('supply_source', value)}
                            placeholder={'Select an exchange'}
                        />
                    </div>
                }
                summary={
                    guaranteed_deal.supply_source ? (
                        <Label>{guaranteed_deal.supply_source}</Label>
                    ) : (
                        <Label placeholder>Select an exchange</Label>
                    )
                }
            />
            <AccordionItem
                onClick={expandPanel('pgDealPrice')}
                expanded={expanded === 'pgDealPrice'}
                readOnly={mode === 'edit'}
                title={'Deal Price'}
                required
                error={showErrors && getGuaranteedDealFieldError('price_local')}
                details={
                    <>
                        <TextFieldFormatter
                            initialValue={guaranteed_deal.price_local || 0}
                            onChange={v => onChangeGuaranteedDeal('price_local', v)}
                            shouldAllowChange={v => !/[a-zA-Z]/.test(v)}
                            formatIn={v => {
                                if (v === 0) {
                                    return '';
                                } else {
                                    return numeral(v).format('0,0.00');
                                }
                            }}
                            formatOut={v => numeral(v).value()}
                            renderInput={({ value, onChange, onBlur }) => (
                                <TextField
                                    disableMargin
                                    errorText={
                                        showErrors && getGuaranteedDealFieldError('price_local')
                                    }
                                    value={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    startAdornment={
                                        <div className="currencyPrefix">{campaign.currency} $</div>
                                    }
                                    required
                                    endAdornment={<Typography>CPM</Typography>}
                                    placeholder="0.00"
                                />
                            )}
                        />
                        <InfoAlertDealPrice />
                    </>
                }
                summary={
                    <>
                        {guaranteed_deal.price_local ? (
                            <Label>
                                {'USD $'}
                                {numeral(guaranteed_deal.price_local).format('0,0.00')} CPM
                            </Label>
                        ) : (
                            <Label placeholder>Enter Deal Price Amount</Label>
                        )}
                        <InfoAlertDealPrice />
                    </>
                }
            />
            <Spacer />
        </React.Fragment>
    );
}
